import { useState } from 'react';
import { salesApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ WHAT YOU READ OUTSIDE THE GATE, AND WHAT YOU EDIT AFTERWARDS ═══
 *
 * The briefing is assembled on the server (VisitBriefing) so the sentences are asserted rather than
 * built out of string concatenation in a component. This file draws them and lets you change the
 * facts they are built from.
 *
 * ⚠ THE BRIEFING IS FIRST, ABOVE EVERYTHING. Not a tab, not below the notes. It is the thing you
 * open thirty seconds before a door opens, and anything that needs a scroll to reach might as well
 * not exist.
 */

export interface Briefing {
  headline: string;
  standing: string;
  whoYouKnow: string[];
  whatHappenedLast: string[];
  outstanding: string[];
  watch: string[];
}

export interface Facts {
  facultyCount: number | null;
  establishedYear: number | null;
  ageInYears: number | null;
  board: string; boardLabel: string;
  management: string; managementLabel: string;
  udiseCode: string | null; aisheCode: string | null; factsSource: string | null;
  incumbent: string; incumbentLabel: string;
  incumbentName: string | null; incumbentRenewalOn: string | null;
  contactCount: number; inputCount: number;
  openObjectionCount: number; openPromiseCount: number;
  hasMetSomeoneWhoCanSign: boolean;
}

export interface Contact {
  id: string; name: string; role: string; roleLabel: string;
  stance: string; stanceLabel: string;
  mobile: string | null; email: string | null; note: string | null;
  hasMet: boolean; canSign: boolean;
}

export interface Point {
  id: string; kind: string; kindLabel: string; state: string; stateLabel: string;
  body: string; saidBy: string | null; raisedOn: string; settledOn: string | null;
  outcome: string | null; isOpen: boolean; isStalePromise: boolean;
}

export const BOARDS = ['Unknown', 'Cbse', 'Icse', 'State', 'Pu', 'University', 'Cambridge', 'Ib', 'Other'];
export const MANAGEMENTS = ['Unknown', 'PrivateUnaided', 'Aided', 'Government', 'Trust', 'Minority', 'Other'];
export const INCUMBENTS = ['Unknown', 'None', 'RegistersAndExcel', 'HomeGrown', 'Competitor'];
export const ROLES = ['Unknown', 'Correspondent', 'Trustee', 'Principal', 'VicePrincipal',
  'OfficeHead', 'Accountant', 'ItStaff', 'Teacher', 'Other'];
export const STANCES = ['Unknown', 'Champion', 'Neutral', 'Sceptic', 'Blocker'];

const H: React.CSSProperties = { fontWeight: 650, fontSize: 12.5, marginBottom: 6 };
const SUB: React.CSSProperties = { fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 };

function List({ title, items, tone }: { title: string; items: string[]; tone?: 'watch' }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {items.map((t, i) => (
          <li key={i} style={{ ...SUB, marginBottom: 3, color: tone === 'watch' ? 'var(--ink)' : 'var(--ink-2)' }}>
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface Score {
  percent: number; headline: string;
  factors: { label: string; points: number; why: string }[];
}

/**
 * ⚠ THE NUMBER IS NEVER SHOWN WITHOUT ITS REASONS, and the reasons are not behind a click. A bare
 * "68%" is a figure you stop trusting the first time it is obviously wrong — and you cannot tell it
 * is wrong without seeing what earned it. The headline is what to DO; the percentage is the
 * smallest thing on the row.
 */
export function ScoreBlock({ s }: { s: Score }) {
  const [open, setOpen] = useState(false);
  const tone = s.percent >= 60 ? 'var(--ok, #067647)' : s.percent >= 35 ? 'var(--ink)' : 'var(--ink-3)';
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 650, fontSize: 13 }}>{s.headline}</span>
        <span style={{ fontSize: 12, color: tone }}>{s.percent}%</span>
        <button className="btn" style={{ padding: '1px 8px', fontSize: 11 }}
                onClick={() => setOpen(!open)}>
          {open ? 'hide why' : 'why'}
        </button>
      </div>
      {open && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
          {s.factors.map((f, i) => (
            <li key={i} style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 2 }}>
              <span style={{
                fontVariantNumeric: 'tabular-nums',
                color: f.points > 0 ? 'var(--ok, #067647)' : 'var(--danger)',
              }}>
                {f.points > 0 ? '+' : ''}{f.points}
              </span>{' '}
              {f.why}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function BriefingBlock({ b }: { b: Briefing }) {
  return (
    <div style={{
      border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16,
      background: 'var(--surface-2, transparent)',
    }}>
      <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 2 }}>{b.headline}</div>
      <div style={{ ...SUB, marginBottom: 12 }}>{b.standing}</div>

      {/* ⚠ WATCH IS FIRST, and it is the only list allowed to say uncomfortable things — "you have
          not met anyone who can sign" is the most valuable sentence this screen can produce, and
          burying it under the pleasant lists is how a deal drifts for nine months. */}
      <List title="Before you go in" items={b.watch} tone="watch" />
      <List title="Who you know there" items={b.whoYouKnow} />
      <List title="Outstanding" items={b.outstanding} />
      <List title="Last few conversations" items={b.whatHappenedLast} />
    </div>
  );
}

/** A compact editor for the facts you would ask about in the first ten minutes. */
export function FactsBlock({ id, facts, onChanged }: {
  id: string; facts: Facts; onChanged: () => void;
}) {
  const [f, setF] = useState({
    facultyCount: facts.facultyCount?.toString() ?? '',
    establishedYear: facts.establishedYear?.toString() ?? '',
    board: facts.board, management: facts.management,
    udiseCode: facts.udiseCode ?? '', aisheCode: facts.aisheCode ?? '',
  });
  const [inc, setInc] = useState({
    system: facts.incumbent,
    name: facts.incumbentName ?? '',
    renewalOn: facts.incumbentRenewalOn ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveFacts = async () => {
    setBusy(true); setError(null);
    try {
      await salesApi.put(`/api/platform/sales/prospects/${id}/facts`, {
        facultyCount: f.facultyCount.trim() ? Number(f.facultyCount) : null,
        establishedYear: f.establishedYear.trim() ? Number(f.establishedYear) : null,
        board: f.board, management: f.management,
        udiseCode: f.udiseCode.trim(), aisheCode: f.aisheCode.trim(),
        // ⚠ Typed by hand, so it is recorded as such. A seeded row says "UDISE 2024-25"; this one
        // says a person, and the briefing is allowed to trust it more.
        factsSource: 'Entered by hand',
      });
      onChanged();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  const saveIncumbent = async () => {
    setBusy(true); setError(null);
    try {
      await salesApi.put(`/api/platform/sales/prospects/${id}/incumbent`, {
        system: inc.system,
        name: inc.name.trim() || null,
        renewalOn: inc.renewalOn || null,
      });
      onChanged();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
      <div style={H}>The school</div>
      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{error}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
        <Small label="Staff" v={f.facultyCount} on={(v) => setF({ ...f, facultyCount: v })} />
        <Small label="Established" v={f.establishedYear} on={(v) => setF({ ...f, establishedYear: v })}
               placeholder="2011" />
        <Pick label="Board" v={f.board} on={(v) => setF({ ...f, board: v })} options={BOARDS} />
        <Pick label="Management" v={f.management} on={(v) => setF({ ...f, management: v })} options={MANAGEMENTS} />
        <Small label="UDISE code" v={f.udiseCode} on={(v) => setF({ ...f, udiseCode: v })} />
        <Small label="AISHE code" v={f.aisheCode} on={(v) => setF({ ...f, aisheCode: v })} />
      </div>
      <button className="btn" disabled={busy} onClick={() => void saveFacts()} style={{ marginBottom: 14 }}>
        Save the school details
      </button>

      {/* ⚠ THE MOST COMMERCIALLY USEFUL FIELD ON THE RECORD, and the one most often left blank
          because asking feels like small talk. Given its own block so it is not skipped. */}
      <div style={H}>What they run today</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
        <Pick label="System" v={inc.system} on={(v) => setInc({ ...inc, system: v })} options={INCUMBENTS} />
        {inc.system === 'Competitor' && (
          <>
            <Small label="Which one" v={inc.name} on={(v) => setInc({ ...inc, name: v })} placeholder="Teachmint" />
            <div>
              <label className="label">Their renewal date</label>
              <input className="input" type="date" value={inc.renewalOn} style={{ width: '100%', marginBottom: 12 }}
                     onChange={(e) => setInc({ ...inc, renewalOn: e.target.value })} />
            </div>
          </>
        )}
      </div>
      {inc.system === 'Competitor' && (
        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '0 0 8px' }}>
          A renewal date turns a school you cannot win today into a diary entry you will win next year.
        </p>
      )}
      <button className="btn" disabled={busy} onClick={() => void saveIncumbent()}>Save</button>
    </div>
  );
}

export function PeopleBlock({ id, contacts, onChanged }: {
  id: string; contacts: Contact[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [c, setC] = useState({ name: '', role: 'Unknown', stance: 'Unknown', mobile: '', note: '', hasMet: true });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      await salesApi.post(`/api/platform/sales/prospects/${id}/contacts`, {
        name: c.name.trim(), role: c.role, stance: c.stance,
        mobile: c.mobile.trim() || null, email: null,
        note: c.note.trim() || null, hasMet: c.hasMet,
      });
      setC({ name: '', role: 'Unknown', stance: 'Unknown', mobile: '', note: '', hasMet: true });
      setAdding(false);
      onChanged();
    } catch { /* the dialog shows the reloaded state either way */ }
    finally { setBusy(false); }
  };

  const remove = async (contactId: string) => {
    await salesApi.del(`/api/platform/sales/prospects/${id}/contacts/${contactId}`).catch(() => undefined);
    onChanged();
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
      <div style={H}>People</div>
      {contacts.length === 0 && (
        <p style={{ ...SUB, margin: '0 0 8px' }}>Nobody recorded yet. Who signs, and who will use it?</p>
      )}
      {contacts.map((p) => (
        <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 5 }}>
          <span style={{ fontWeight: 600, fontSize: 12.5 }}>{p.name}</span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {p.roleLabel}{p.stanceLabel ? ` · ${p.stanceLabel.toLowerCase()}` : ''}
            {p.canSign ? ' · can sign' : ''}{p.hasMet ? '' : ' · not met'}
          </span>
          <button className="btn" style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11 }}
                  onClick={() => void remove(p.id)}>✕</button>
        </div>
      ))}

      {adding ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <Small label="Name" v={c.name} on={(v) => setC({ ...c, name: v })} autoFocus />
            <Small label="Phone" v={c.mobile} on={(v) => setC({ ...c, mobile: v })} />
            <Pick label="Role" v={c.role} on={(v) => setC({ ...c, role: v })} options={ROLES} />
            <Pick label="How they seem" v={c.stance} on={(v) => setC({ ...c, stance: v })} options={STANCES} />
          </div>
          <Small label="Anything useful" v={c.note} on={(v) => setC({ ...c, note: v })}
                 placeholder="Has kept the fee registers for eleven years." />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, marginBottom: 10 }}>
            <input type="checkbox" checked={c.hasMet} onChange={(e) => setC({ ...c, hasMet: e.target.checked })} />
            I have actually met them
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn btn-accent" disabled={!c.name.trim() || busy} onClick={() => void add()}>Add</button>
          </div>
        </div>
      ) : (
        <button className="btn" onClick={() => setAdding(true)} style={{ marginTop: 6 }}>+ Add someone</button>
      )}
    </div>
  );
}

export function PointsBlock({ id, points, onChanged }: {
  id: string; points: Point[]; onChanged: () => void;
}) {
  const [kind, setKind] = useState('Input');
  const [body, setBody] = useState('');
  const [saidBy, setSaidBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settling, setSettling] = useState<{ pointId: string; state: string } | null>(null);
  const [outcome, setOutcome] = useState('');

  const raise = async () => {
    setBusy(true); setError(null);
    try {
      await salesApi.post(`/api/platform/sales/prospects/${id}/points`, {
        kind, body: body.trim(), raisedOn: null, saidBy: saidBy.trim() || null,
      });
      setBody(''); setSaidBy('');
      onChanged();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  const settle = async (pointId: string, state: string, text: string | null) => {
    setBusy(true); setError(null);
    try {
      await salesApi.post(`/api/platform/sales/prospects/${id}/points/${pointId}/settle`,
        { state, outcome: text });
      setSettling(null); setOutcome('');
      onChanged();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
      <div style={H}>What they said</div>
      <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '0 0 10px' }}>
        {/* ⚠ Stated on the screen because it changes what gets entered. A founder who knows why
            inputs matter records them; one who thinks it is a wishlist does not. */}
        Every “can it also do X” is a buying signal — a school that asks is a school imagining using it.
      </p>
      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{error}</p>}

      {points.map((p) => (
        <div key={p.id} style={{ marginBottom: 7 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{
              fontSize: 11, color: p.isStalePromise ? 'var(--danger)' : 'var(--ink-3)', minWidth: 82,
            }}>
              {p.kindLabel}
            </span>
            <span style={{ fontSize: 12.5, textDecoration: p.isOpen ? 'none' : 'line-through', flex: 1 }}>
              {p.body}
              {p.saidBy ? <span style={{ color: 'var(--ink-3)' }}> — {p.saidBy}</span> : null}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.isOpen ? '' : p.stateLabel}</span>
            {p.isOpen && (
              <select className="input" value="" style={{ width: 'auto', fontSize: 11, padding: '2px 6px' }}
                onChange={(e) => {
                  const state = e.target.value;
                  if (!state) return;
                  // ⚠ A delivered promise asks for a sentence, because the domain refuses it without
                  // one — better to ask here than to show a red error after the click.
                  if (p.kind === 'Promise' && state === 'Delivered') setSettling({ pointId: p.id, state });
                  else void settle(p.id, state, null);
                }}>
                <option value="">Settle…</option>
                <option value="Answered">Answered</option>
                <option value="Building">Being built</option>
                <option value="Delivered">Delivered</option>
                <option value="Declined">Declined</option>
              </select>
            )}
          </div>
          {settling?.pointId === p.id && (
            <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
              <input className="input" placeholder="What was delivered?" value={outcome} autoFocus
                     style={{ flex: 1 }} onChange={(e) => setOutcome(e.target.value)} />
              <button className="btn btn-accent" disabled={!outcome.trim() || busy}
                      onClick={() => void settle(p.id, settling.state, outcome.trim())}>Save</button>
              <button className="btn" onClick={() => setSettling(null)}>Cancel</button>
            </div>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <select className="input" value={kind} style={{ width: 'auto' }} onChange={(e) => setKind(e.target.value)}>
          <option value="Input">They asked for</option>
          <option value="Objection">Objection</option>
          <option value="Promise">We promised</option>
        </select>
        <input className="input" placeholder="In their words" value={body} style={{ flex: '2 1 220px' }}
               onChange={(e) => setBody(e.target.value)} />
        <input className="input" placeholder="Who said it" value={saidBy} style={{ flex: '1 1 120px' }}
               onChange={(e) => setSaidBy(e.target.value)} />
        <button className="btn" disabled={!body.trim() || busy} onClick={() => void raise()}>Add</button>
      </div>
    </div>
  );
}

// ---- small shared inputs ----

function Small({ label, v, on, placeholder, autoFocus }: {
  label: string; v: string; on: (v: string) => void; placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={v} placeholder={placeholder} autoFocus={autoFocus}
             style={{ width: '100%', marginBottom: 12 }} onChange={(e) => on(e.target.value)} />
    </div>
  );
}

function Pick({ label, v, on, options }: {
  label: string; v: string; on: (v: string) => void; options: string[];
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={v} style={{ width: '100%', marginBottom: 12 }}
              onChange={(e) => on(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{spaced(o)}</option>)}
      </select>
    </div>
  );
}

/** "RegistersAndExcel" → "Registers and excel". ⚠ The API speaks enum names; a person does not. */
function spaced(s: string): string {
  const out = s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/And/g, 'and');
  return out.charAt(0) + out.slice(1).toLowerCase();
}
